import React from 'react'
import { useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import {
	Table,
	TableColumnsType,
	Space,
	Button,
	Typography,
	Image,
	Badge,
	Modal,
	// ConfigProvider,
	Switch,
	Popover,
} from 'antd'
import { EyeOutlined } from '@ant-design/icons'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'dayjs/locale/ko'

import vendorAtom from '@src/atom/vendor'

import { IMG_STORAGE_URL } from '@src/utils/constants'
import { getDateByTimezone, priceFormatter } from '@src/utils/utils'
import QuestionMarkIcon from '@src/components/icon/QuestionMarkIcon'
import { useChallengeListPage } from './hooks'
import { CompanyChallengeTableItem, CompanyChallengeTableCell } from './type'
import {
	HotdealChallengeModificationModal,
	HotdealChallengePreviewModal,
	CompanyPreviewModal,
} from './components'

const { Text, Title } = Typography

export const ChallengeListPage: React.FC = () => {
	const {
		pagination,
		isFetchingCompanyChallenges,
		tableItems,
		isModificationModalOpened,
		topicOptions,
		isEtcTopicModalOpened,
		onEtcTopicModalCanceled,
		selectedTopicName,
		onModificationModalCanceled,
		isChallengePreviewModalOpened,
		onChallengePreviewModalCanceled,
		form,
		onFormChanged,
		hasThumbnailError,
		hasStartDateError,
		startDateErrorMessage,
		thumbnail,
		onThumbnailUploaded,
		onThumbnailRemoved,
		descriptionImages,
		onDescriptionImageUploaded,
		onDescriptionImageRemoved,
		startDate,
		onStartDateChanged,
		modificationModalBodyRef,
		selectedTableItem,
		canDraftHotdealChallenge,
		canCreateHotdealChallenge,
		canModifyHotdealChallenge,
		onDraftClicked,
		onGuidedDraftClicked,
		onCreateClicked,
		onModifyClicked,
		isDrafting,
		isCreating,
		isModifying,
		isModificationWarnModalOpened,
		onModificationModalCloseConfirmed,
		onModificationWarnModalCanceled,
		isCardRequireModalOpened,
		onCardRequireModalCanceled,
		isCompanyPreviewModalOpened,
		onCompanyPreviewModalCanceled,
		companyInfo,
		isAllowModalOpened,
		onAllowModalCanceled,
	} = useChallengeListPage()

	return (
		<>
			<Container>
				<Title level={3} style={{ marginTop: '20px' }}>
					등록 챌린지 조회
				</Title>
				{/* <TableActionArea>
					<AllowButtonWrapper>
						<ConfigProvider
							theme={{
								token: {
									colorPrimary: '#4E4E4E',
								},
							}}
						>
							<Button
								type="primary"
								style={{ width: '100px' }}
								onClick={() => setIsAllowModalOpened(true)}
							>
								1:1 문의하기
							</Button>
						</ConfigProvider>
						<AllowTooltip>모티너스 앱에 노출 원할 경우? 👇</AllowTooltip>
					</AllowButtonWrapper>
				</TableActionArea> */}
				<Table<CompanyChallengeTableItem>
					rowKey="id"
					pagination={pagination}
					columns={ChallengeListTableColumns}
					dataSource={tableItems}
					loading={isFetchingCompanyChallenges}
					bordered
				/>
			</Container>

			<HotdealChallengeModificationModal
				startDate={startDate}
				onStartDateChanged={onStartDateChanged}
				isModificationModalOpened={isModificationModalOpened}
				onModificationModalCanceled={onModificationModalCanceled}
				modificationModalBodyRef={modificationModalBodyRef}
				form={form}
				onFormChanged={onFormChanged}
				thumbnail={thumbnail}
				onThumbnailUploaded={onThumbnailUploaded}
				onThumbnailRemoved={onThumbnailRemoved}
				descriptionImages={descriptionImages}
				onDescriptionImageUploaded={onDescriptionImageUploaded}
				onDescriptionImageRemoved={onDescriptionImageRemoved}
				hasThumbnailError={hasThumbnailError}
				hasStartDateError={hasStartDateError}
				startDateErrorMessage={startDateErrorMessage}
				selectedTableItem={selectedTableItem}
				canDraftHotdealChallenge={canDraftHotdealChallenge}
				canCreateHotdealChallenge={canCreateHotdealChallenge}
				canModifyHotdealChallenge={canModifyHotdealChallenge}
				onDraftClicked={onDraftClicked}
				onGuidedDraftClicked={onGuidedDraftClicked}
				onCreateClicked={onCreateClicked}
				onModifyClicked={onModifyClicked}
				isDrafting={isDrafting}
				isCreating={isCreating}
				isModifying={isModifying}
				isModificationWarnModalOpened={isModificationWarnModalOpened}
				onModificationModalCloseConfirmed={onModificationModalCloseConfirmed}
				onModificationWarnModalCanceled={onModificationWarnModalCanceled}
				priceFormatter={priceFormatter}
				isCardRequireModalOpened={isCardRequireModalOpened}
				onCardRequireModalCanceled={onCardRequireModalCanceled}
				topicOptions={topicOptions}
				isEtcTopicModalOpened={isEtcTopicModalOpened}
				onEtcTopicModalCanceled={onEtcTopicModalCanceled}
				selectedTopicName={selectedTopicName}
			/>

			<HotdealChallengePreviewModal
				isChallengePreviewModalOpened={isChallengePreviewModalOpened}
				onChallengePreviewModalCanceled={onChallengePreviewModalCanceled}
				selectedTableItem={selectedTableItem}
				priceFormatter={priceFormatter}
			/>

			<Modal
				open={isAllowModalOpened}
				centered
				closable={false}
				title={<AllowModalHeader />}
				footer={<AllowModalFooter onAllowModalCanceled={onAllowModalCanceled} />}
				onCancel={onAllowModalCanceled}
			>
				<AllowModalBody />
			</Modal>

			<CompanyPreviewModal
				isCompanyPreviewModalOpened={isCompanyPreviewModalOpened}
				onCompanyPreviewModalCanceled={onCompanyPreviewModalCanceled}
				companyInfo={companyInfo}
			/>
		</>
	)
}

const allowModalHeader: React.FC = () => (
	<ModalHeaderWrapper>
		<ModalHeaderTitle>챌린지 노출을 원할 경우, 아래 번호로 연락주세요.</ModalHeaderTitle>
		<ModalHeaderSubTitle>(평일 10:00~18:00 까지)</ModalHeaderSubTitle>
	</ModalHeaderWrapper>
)

const AllowModalHeader = React.memo(allowModalHeader)

const allowModalBody: React.FC = () => (
	<ModalBodyWrapper>
		<ModalBodyContact>📞 : 010-9768-2105</ModalBodyContact>
		<ModalBodyUrgentContact>📞 : (긴급 전화) 010-3454-4430</ModalBodyUrgentContact>
	</ModalBodyWrapper>
)

const AllowModalBody = React.memo(allowModalBody)

const allowModalFooter: React.FC<{
	onAllowModalCanceled: () => void
}> = ({ onAllowModalCanceled }) => (
	<Button type="primary" onClick={onAllowModalCanceled}>
		확인
	</Button>
)

const AllowModalFooter = React.memo(allowModalFooter)

const titleCell: CompanyChallengeTableCell = (_, { hotdealItemImage, challengeName, isTemp }) => {
	return (
		<ChallengeNameCell>
			<HotDealItemImageWrapper>
				{hotdealItemImage ? (
					<Image
						src={`${IMG_STORAGE_URL}/hotdeal/hotdeal_product_img/${hotdealItemImage}`}
						style={{ width: '100%', height: '100%', objectFit: 'cover' }}
						wrapperStyle={{ width: '100%', height: '100%' }}
						preview={{
							mask: <EyeOutlined />,
						}}
					/>
				) : null}
			</HotDealItemImageWrapper>
			<ChallengeNameWrapper>
				{challengeName ? <Text>{challengeName}</Text> : null}
				{isTemp ? (
					<Badge
						count="임시저장"
						color="#EFEFEF"
						style={{ color: '#4E4E4E', height: '24px', lineHeight: '24px', fontWeight: '600' }}
					/>
				) : null}
			</ChallengeNameWrapper>
		</ChallengeNameCell>
	)
}

const durationCell: CompanyChallengeTableCell = (
	_,
	{ exposureStartDate, certificationEndDate },
) => {
	if (exposureStartDate === null || certificationEndDate === null) {
		return null
	}

	return (
		<Space direction="horizontal">
			<Text>{getDateByTimezone('Asia/Seoul', 'yyyy-MM-dd', exposureStartDate)}</Text>
			<Text>~</Text>
			<Text>{getDateByTimezone('Asia/Seoul', 'yyyy-MM-dd', certificationEndDate)}</Text>
		</Space>
	)
}

const PreviewCell: CompanyChallengeTableCell = (_, { canPreview, onClickPreview }) => {
	return (
		<Button disabled={!canPreview} onClick={onClickPreview}>
			보기
		</Button>
	)
}

const ShareCell: CompanyChallengeTableCell = (_, { canLink, onClickLink }) => {
	return (
		<Button disabled={!canLink} onClick={onClickLink}>
			링크
		</Button>
	)
}

const ExposureCell: CompanyChallengeTableCell = (
	_,
	{ isTemp, isExposure, status, onChangeExposure },
) => {
	const disabled = !!(status === 'approved' || isTemp)
	return (
		<CustomSwitch
			onChange={onChangeExposure}
			defaultChecked={isExposure}
			disabled={disabled}
			isActive={isExposure}
		/>
	)
}

const ActionCell: CompanyChallengeTableCell = (
	_,
	{
		canCopy,
		canModify,
		canDelete,
		onClickCopy,
		onClickModify,
		onClickDelete,
		isCopying,
		isModifying,
		isDeleting,
	},
) => {
	return (
		<Space direction="horizontal" size="large">
			<Button disabled={!canCopy} onClick={onClickCopy} loading={isCopying} type="primary">
				복제
			</Button>
			<Button disabled={!canModify} onClick={onClickModify} loading={isModifying}>
				수정
			</Button>
			<Button
				disabled={!canDelete}
				onClick={onClickDelete}
				loading={isDeleting}
				type="primary"
				danger
			>
				삭제
			</Button>
		</Space>
	)
}

const BrandPageExposureTitle = () => {
	const vendor = useRecoilValue(vendorAtom)
	return (
		<TabelHeaderTitle>
			브랜드 페이지 노출
			<Popover
				content={
					<div>
						<InfoPopupDesc>브랜드 페이지에 챌린지가 노출돼요!</InfoPopupDesc>
						<InfoPopupDescLink
							href={`https://preview.motiners.com?path=${vendor.username}`}
							target="_blank"
						>
							브랜드 페이지 바로 가기
						</InfoPopupDescLink>
					</div>
				}
				placement="bottom"
				trigger="hover"
				arrow={false}
			>
				<QuestionMarkIcon color=" #b0b8c1" />
			</Popover>
		</TabelHeaderTitle>
	)
}

const ManinExposureStatusTitle = () => {
	return (
		<TabelHeaderTitle>
			모티너스 노출
			<Popover
				content={<InfoPopupDesc>모티너스 앱 메인페이지에 노출 돼요!</InfoPopupDesc>}
				placement="bottom"
				trigger="hover"
				arrow={false}
			>
				<QuestionMarkIcon color=" #b0b8c1" />
			</Popover>
		</TabelHeaderTitle>
	)
}

const ChallengeListTableColumns: TableColumnsType<CompanyChallengeTableItem> = [
	{
		title: '번호',
		align: 'center',
		dataIndex: 'challengeId',
		width: '6%',
	},
	{
		title: '챌린지 명',
		align: 'center',
		render: titleCell,
		width: '25%',
	},
	{
		title: '기간',
		align: 'center',
		render: durationCell,
		width: '18%',
	},
	{
		title: '미리보기',
		align: 'center',
		render: PreviewCell,
		width: '6%',
	},
	{
		title: '공유하기',
		align: 'center',
		render: ShareCell,
		width: '6%',
	},
	{
		title: ' ',
		align: 'center',
		render: ActionCell,
		width: '20%',
	},
	{
		// title: '브랜드 페이지 노출',
		title: <BrandPageExposureTitle />,
		align: 'center',
		render: ExposureCell,
		width: '8%',
	},
	{
		title: <ManinExposureStatusTitle />,
		align: 'center',
		dataIndex: 'exposureStatus',
		width: '6%',
	},
]

const Container = styled.div`
	padding: 0 36px;
	max-width: 1440px;
	margin-bottom: 48px;
`

const TableActionArea = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	margin-bottom: 8px;
`

const AllowButtonWrapper = styled.div`
	position: relative;
`

const AllowTooltip = styled.div`
	background: #ffffff;
	color: #121212;
	position: absolute;
	box-shadow: 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px 0px rgba(0, 0, 0, 0.08),
		0px 2px 14px 8px rgba(0, 0, 0, 0.05);
	border-radius: 5px;
	padding: 12px;
	width: 250px;
	right: 0;
	top: -50px;
	font-size: 16px;
	font-weight: 500;
	display: flex;
	flex-direction: row;
	justify-content: center;

	&::after {
		content: ' ';
		position: absolute;
		top: calc(100% - 4px);
		right: 45px;
		border-width: 4px;
		border-style: solid;
		border-color: #ffffff;
		border-bottom-right-radius: 3px;
		transform: rotate(45deg);
	}
`

const ChallengeNameCell = styled.div`
	width: 100%;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
`

const HotDealItemImageWrapper = styled.div`
	width: 54px;
	height: 54px;
	background: #d9d9d9;
	border-radius: 8px;
	overflow: hidden;
	flex-grow: 0;
	flex-shrink: 0;
`

const ChallengeNameWrapper = styled.div`
	flex-grow: 1;
	display: flex;
	flex-direction: row;
	gap: 8px;
	align-items: center;
`

const ModalHeaderWrapper = styled.div`
	display: flex;
	flex-direction: column;
`

const ModalHeaderTitle = styled.div`
	color: #121212;
	font-size: 18px;
	font-weight: 600;
`

const ModalHeaderSubTitle = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
`

const ModalBodyWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 24px;
`

const ModalBodyContact = styled.div`
	color: #121212;
	font-size: 14px;
	font-weight: 500;
`

const ModalBodyUrgentContact = styled.div`
	color: #910000;
	font-size: 14px;
	font-weight: 500;
`

const TabelHeaderTitle = styled.span`
	text-align: center;
`

const infoPopupDescStyle = css`
	display: block;
	font-family: 'Pretendard';
	font-weight: 600;
	font-size: 1.4rem;
	line-height: 2.1rem;
`

const InfoPopupDesc = styled.span`
	${infoPopupDescStyle}
`

const InfoPopupDescLink = styled.a`
	${infoPopupDescStyle}
	color: #2F80ED;
	text-decoration: underline;
	text-underline-position: 'under';
`

const CustomSwitch = styled(Switch)<{ isActive: boolean }>`
	:disabled {
		${(props) =>
			props.isActive
				? css`
						background-color: #acccf7;
				  `
				: css`
						background-color: rgba(0, 0, 0, 0.1);
				  `}
	}
`
